import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { sb } from '../supabase';

// /track/:token — the tracking button on managed_out_for_delivery.
//
// PUBLIC. No login, so it must reveal nothing: this page never displays a
// reference, a carrier name, a tracking number, an address or a status. It has
// exactly two outcomes — redirect to the carrier, or say the shipment is on its
// way and stop.
//
// get_shipment_redirect returns a destination URL or null and NOTHING else
// (migration 20260914095916). There is no second field to leak through, so
// there is nothing here to accidentally render.
//
// The token is NOT requests.id. It is a separate 122-bit handle, because a
// public URL must not carry the primary key of a login-gated resource and
// because it can be rotated if a link leaks — see the migration for the full
// reasoning.
const T = {
  ar: {
    onItsWay: 'شحنتك في الطريق',
    openOrder: 'افتح طلبك',
    checking: '…',
  },
  en: {
    onItsWay: 'Your shipment is on its way',
    openOrder: 'Open your order',
    checking: '…',
  },
  zh: {
    onItsWay: '您的货物正在途中',
    openOrder: '打开我的订单',
    checking: '…',
  },
};

export default function TrackShipment({ lang = 'ar' }) {
  const { token } = useParams();
  const isAr = lang === 'ar';
  const t = T[lang] || T.ar;
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // Suppress the Referer BEFORE navigating. Without this the carrier (DHL,
    // Aramex, 17track…) receives `Referer: https://maabar.io/track/<token>` and
    // now holds a working link to this shipment. The token already limits what
    // that is worth; sending it anyway would be careless.
    //
    // Injected here rather than in index.html so the policy is scoped to this
    // page instead of quietly changing referrer behaviour for the whole site.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);

    (async () => {
      const { data, error } = await sb.rpc('get_shipment_redirect', { p_token: token });
      if (cancelled) return;
      if (error) {
        // Never surface the reason: an error message is information about a
        // resource the caller has not authenticated for. Log it and fall
        // through to the minimal page.
        console.error('[track] get_shipment_redirect failed:', error.message);
        setChecking(false);
        return;
      }
      const url = typeof data === 'string' ? data.trim() : '';
      // Re-check the scheme client-side too. The function already filters, but
      // this is the line that actually hands a URL to the browser.
      if (/^https?:\/\//i.test(url)) {
        // replace, not assign: the token URL should not sit in history where a
        // shared device would surface it.
        window.location.replace(url);
        return;
      }
      setChecking(false);
    })();

    return () => {
      cancelled = true;
      if (meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, [token]);

  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} style={{
      minHeight: '70vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 18, padding: '40px 20px',
      textAlign: 'center',
    }}>
      {checking ? (
        <p style={{ fontSize: 14, color: 'var(--text-muted)', fontFamily: font }}>{t.checking}</p>
      ) : (
        <>
          <p style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0, fontFamily: font }}>
            {t.onItsWay}
          </p>
          {/* Deliberately the generic dashboard, not a deep link: this page does
              not know the request id, and learning it would defeat the token. */}
          <a href="/dashboard?tab=requests" rel="noreferrer" style={{
            fontSize: 14, color: 'var(--text-secondary)', textDecoration: 'underline', fontFamily: font,
          }}>
            {t.openOrder}
          </a>
        </>
      )}
    </div>
  );
}
